import { Vector2 } from 'app/contracts/Geometry';
import { DirectionType, AxisType } from 'app/enum/Geometry';
import { device } from 'utils/device';

/*
 *  Motion direction. Normalise quantrants into up/down/left/right
 */

export class Direction {
	private _slope: number;
	private _fDistance: number;
	private _input: Vector2 = { x: 0, y: 0 };
	private _lastCords: Vector2 = { x: 0, y: 0 };
	private _innerWidth: number;
	private _sensitivity: number;
	private _octant: number; //1-8
	private _direction: DirectionType;
	private _axis: AxisType; //SWIPE AXIS

	private _needsMoveCalculation: boolean; //If needs to work out move based on Andle
	private _moveAxis: AxisType; //Movement type
	private _moveStart: Vector2; //Point of Touch Start / End
	private _pixelMoveThreshold: number;
	private _moveAngle: number;

	private _onUpdate: CallableFunction = null;
	private _onUpdateMove: CallableFunction = null;


	constructor(sensitivity: number = 30, pixelMoveThreshold = 10) {
		this.resize();
		// higher the dividing factor, higher the sensitivity - increasing this means swipe has to be smaller for it to not be registered as NULL
		this._sensitivity = sensitivity;
		this._pixelMoveThreshold = pixelMoveThreshold;
	}

	public set onUpdate(v: CallableFunction) {
		this._onUpdate = v;
	}

	public get octant(): number {
		return this._octant;
	}

	public get direction(): DirectionType {
		return this._direction;
	}

	public get axis(): AxisType {
		return this._axis;
	}

	public get moveAxis(): AxisType {
		return this._moveAxis;
	}

	public resize(): void {
		this._innerWidth = device.innerWidth;
	}

	public reset(cords: Vector2): void {
		this._lastCords.x = cords.x;
		this._lastCords.y = cords.y;
		this._moveAxis = null;
	}

	public calculate(cords: Vector2): DirectionType {
		this._octants(cords);

		if (this._needsMoveCalculation) {
			this._angle(cords);
		}

		return this._direction;
	}

	public pointerDown(cords: Vector2): void {
		this._needsMoveCalculation = true;
		this._moveStart = cords;
		this._moveAxis = null;
	}

	public pointerUp(): void {
		this._needsMoveCalculation = false;
	}

	/**
	 * Get angle. Assumes 0% is east point
	 * @param angle
	 * @param thresholdDegrees
	 * @returns
	 */
	public angleWithinHorizontalSwipeArea(angle: number, thresholdDegrees: number = 30): boolean {
		if (angle >= 360 - thresholdDegrees && angle <= 360) {
			return true;
		} else if (angle >= 0 && angle <= 0 + thresholdDegrees) {
			return true;
		} else if (angle >= 180 && angle <= 180 + thresholdDegrees) {
			return true;
		} else if (angle >= 180 - thresholdDegrees && angle <= 180) {
			return true;
		}

		return false;
	}

	private _angle(cords: Vector2): void {
		this._moveAngle = (Math.atan2(cords.y - this._moveStart.y, cords.x - this._moveStart.x) * 180) / Math.PI;
		if (this._moveAngle < 0) {
			this._moveAngle += 360;
		}

		this._moveAxis = this.angleWithinHorizontalSwipeArea(this._moveAngle) ? AxisType.HORIZONTAL : AxisType.VERTICAL;

		//If more than X/Y:
		const reachedThreshold =
			Math.abs(cords.x - this._moveStart.x) >= this._pixelMoveThreshold ||
			Math.abs(cords.y - this._moveStart.y) >= this._pixelMoveThreshold;

		if (reachedThreshold) {
			if (this._onUpdateMove) {
				this._onUpdateMove(this._moveAxis);
			}

			this._needsMoveCalculation = false;
		}
	}

	private _octants(cords: Vector2): DirectionType {
		// calculate the slope of the swipe
		this._input.x = cords.x - this._lastCords.x;
		this._input.y = cords.y - this._lastCords.y;
		this._slope = this._input.y / this._input.x;

		// calculate the distance of tap start and end
		this._fDistance = Math.sqrt(Math.pow(this._input.x, 2) + Math.pow(this._input.y, 2));

		this._lastCords.x = cords.x;
		this._lastCords.y = cords.y;
		this._direction = DirectionType.NULL;
		this._axis = AxisType.NULL;

		if (this._fDistance <= this._innerWidth / this._sensitivity) {
			return;
		}

		if (this._input.x >= 0 && this._input.y > 0 && this._slope > 1) {
			// first octant (0 to 45)
			this._octant = 1;
			this._direction = DirectionType.DOWN;
		} else if (this._input.x <= 0 && this._input.y > 0 && this._slope < -1) {
			// eighth octant (0 to -45)
			this._octant = 8;
			this._direction = DirectionType.DOWN;
		} else if (this._input.x > 0 && this._input.y >= 0 && this._slope < 1 && this._slope >= 0) {
			// second octant RIGHT (45 to 90)
			this._octant = 2;
			this._direction = DirectionType.LEFT;
		} else if (this._input.x > 0 && this._input.y <= 0 && this._slope > -1 && this._slope <= 0) {
			// third octant RIGHT (90 to 135)
			this._octant = 3;
			this._direction = DirectionType.LEFT;
		} else if (this._input.x < 0 && this._input.y >= 0 && this._slope > -1 && this._slope <= 0) {
			// seventh octant LEFT (-45 to -90)
			this._octant = 7;
			this._direction = DirectionType.RIGHT;
		} else if (this._input.x < 0 && this._input.y <= 0 && this._slope >= 0 && this._slope < 1) {
			// sixth octant LEFT (-90 to -135)
			this._octant = 6;
			this._direction = DirectionType.RIGHT;
		} else if (this._input.x >= 0 && this._input.y < 0 && this._slope < -1) {
			// fourth octant DUCK (135 to 180)
			this._octant = 4;
			this._direction = DirectionType.UP;
		} else if (this._input.x <= 0 && this._input.y < 0 && this._slope > 1) {
			// fifth octant DUCK
			this._octant = 5;
			this._direction = DirectionType.UP;
		}

		if (this._direction === DirectionType.RIGHT || this._direction === DirectionType.LEFT) {
			this._axis = AxisType.HORIZONTAL;
		} else if (this._direction === DirectionType.UP || this._direction === DirectionType.DOWN) {
			this._axis = AxisType.VERTICAL;
		}

		if (this._onUpdate) {
			this._onUpdate(this._direction);
		}

		return this._direction;
	}
}
